import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { COLORS } from "../../../values/colors";
import { StyledTab } from "../../base/Navbar/styles";
import UserMenu from "../../base/Navbar/UserMenu";
import {
  CloseButton,
  CloseButtonWrapper,
  FullWidthDrawer,
  MenuButton,
  StyledCloseIcon,
  StyledListItem,
  StyledMenuIcon,
} from "../../base/Navbar/BurgerMenu/styles";
import {
  ActionsGroup,
  CategoryTabs,
  CategoryMenu,
  CategoryMenuItem,
  CategoryMenuList,
  CategoryMenuButton,
  CategoryNav,
  CartInfo,
  CartTrigger,
  LeftGroup,
  NavContainer,
  LogoButton,
  NavInner,
} from "./shopNavbarStyles";
import { getShopPathForCategory, SHOP_NAV_ITEMS, ShopNavbarCategoryItem } from "./shopNavigation";

interface ShopNavbarProps {
  cartItemsCount: number;
  onCartClick: () => void;
  categories?: readonly ShopNavbarCategoryItem[];
  activeCategory?: string | null;
  onCategorySelect?: (category: string) => void;
}

const ShopNavbar = ({
  cartItemsCount,
  onCartClick,
  categories,
  activeCategory,
  onCategorySelect,
}: ShopNavbarProps) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLElement | null>(null);
  const navRef = useRef<HTMLDivElement | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 1200 : false));
  const [compactUserMenu, setCompactUserMenu] = useState(false);
  const resolvedCategories = categories ?? SHOP_NAV_ITEMS;
  const directTabs = useMemo(
    () => resolvedCategories.filter((category): category is ShopNavbarCategoryItem & { value: string } => !category.children && Boolean(category.value)),
    [resolvedCategories]
  );
  const nestedCategories = useMemo(
    () => resolvedCategories.filter((category) => category.children && category.children.length > 0),
    [resolvedCategories]
  );

  useEffect(() => {
    if (!openMenu) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!navRef.current?.contains(event.target as Node)) {
        setOpenMenu(null);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenMenu(null);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openMenu]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const syncViewportState = () => {
      const mobile = window.innerWidth < 1200;
      setIsMobile(mobile);
      setCompactUserMenu(mobile);
      if (!mobile) {
        setMobileMenuOpen(false);
      }
    };
    syncViewportState();
    window.addEventListener("resize", syncViewportState);

    return () => {
      window.removeEventListener("resize", syncViewportState);
    };
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
        setOpenMenu(null);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleScroll = () => {
      if (!containerRef.current) {
        return;
      }

      if (isMobile) {
        containerRef.current.style.transition = "background-color 0.4s ease";
        containerRef.current.style.backgroundColor = COLORS.darkdarkGray;
        return;
      }

      if (window.scrollY > 0) {
        containerRef.current.style.transition = "background-color 0.8s ease";
        containerRef.current.style.backgroundColor = "black";
      } else {
        containerRef.current.style.transition = "background-color 0.4s ease";
        containerRef.current.style.backgroundColor = "transparent";
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMobile]);

  const closeAllMenus = () => {
    setOpenMenu(null);
    setMobileMenuOpen(false);
  };

  const handleCategorySelect = (category: string) => {
    if (onCategorySelect) {
      onCategorySelect(category);
      closeAllMenus();
      return;
    }

    navigate(getShopPathForCategory(category, activeCategory));
    closeAllMenus();
  };

  const handleTabChange = (_event: SyntheticEvent, newValue: string) => {
    handleCategorySelect(newValue);
  };

  return (
    <>
      <NavContainer ref={containerRef}>
        <NavInner>
          <LeftGroup>
            <LogoButton
              type="button"
              aria-label="Zum Shop"
              onClick={() => {
                navigate("/shop");
                closeAllMenus();
              }}
            />
            {!isMobile && resolvedCategories.length > 0 ? (
              <CategoryNav ref={navRef}>
                {directTabs.length > 0 ? (
                  <CategoryTabs
                    value={directTabs.some((category) => category.value === activeCategory) ? activeCategory : false}
                    onChange={handleTabChange}
                    textColor="inherit"
                    TabIndicatorProps={{
                      style: { backgroundColor: "transparent" },
                    }}
                    sx={{ color: COLORS.white }}
                  >
                    {directTabs.map((category) => (
                      <StyledTab key={category.value} label={category.label} value={category.value} />
                    ))}
                  </CategoryTabs>
                ) : null}
                {nestedCategories.map((category) => {
                  const isActive =
                    category.value === activeCategory ||
                    category.children?.some((child) => child.value === activeCategory) === true;
                  const isOpen = openMenu === category.label;

                  return (
                    <CategoryMenu key={category.label}>
                      <CategoryMenuButton
                        type="button"
                        $active={isActive}
                        $open={isOpen}
                        onClick={() => setOpenMenu((current) => (current === category.label ? null : category.label))}
                      >
                        {category.label}
                      </CategoryMenuButton>
                      <CategoryMenuList $open={isOpen}>
                        {category.children?.map((item) => (
                          <CategoryMenuItem
                            key={item.value}
                            type="button"
                            $active={item.value === activeCategory}
                            onClick={() => handleCategorySelect(item.value)}
                          >
                            {item.label}
                          </CategoryMenuItem>
                        ))}
                      </CategoryMenuList>
                    </CategoryMenu>
                  );
                })}
              </CategoryNav>
            ) : null}
          </LeftGroup>
          <ActionsGroup>
            <CartTrigger type="button" onClick={onCartClick}>
              <ShoppingCartIcon fontSize="small" />
              <CartInfo>Warenkorb ({cartItemsCount})</CartInfo>
            </CartTrigger>
            <UserMenu compact={compactUserMenu} />
            {isMobile && resolvedCategories.length > 0 ? (
              <MenuButton
                type="button"
                aria-label="Shop-Menü öffnen"
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(true)}
              >
                <StyledMenuIcon />
              </MenuButton>
            ) : null}
          </ActionsGroup>
        </NavInner>
      </NavContainer>
      <FullWidthDrawer anchor="right" open={isMobile && mobileMenuOpen} onClose={closeAllMenus}>
        <CloseButtonWrapper>
          <CloseButton type="button" aria-label="Shop-Menü schließen" onClick={closeAllMenus}>
            <StyledCloseIcon />
          </CloseButton>
        </CloseButtonWrapper>
        <Box role="presentation" onClick={closeAllMenus}>
          <Box sx={{ padding: "1.5rem" }}>
            <List>
              {resolvedCategories.length > 0
                ? resolvedCategories.map((category) => {
                const isActive =
                  category.value === activeCategory ||
                  category.children?.some((child) => child.value === activeCategory) === true;

                if (category.children && category.children.length > 0) {
                  return category.children.map((item) => (
                    <StyledListItem key={item.value} disablePadding>
                      <ListItemButton onClick={() => handleCategorySelect(item.value)}>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: "1.5rem",
                            fontWeight: item.value === activeCategory ? 700 : 400,
                          }}
                        />
                      </ListItemButton>
                    </StyledListItem>
                  ));
                }

                if (!category.value) {
                  return null;
                }

                return (
                  <StyledListItem key={category.value} disablePadding>
                    <ListItemButton onClick={() => handleCategorySelect(category.value!)}>
                      <ListItemText
                        primary={category.label}
                        primaryTypographyProps={{
                          fontSize: "1.5rem",
                          fontWeight: isActive ? 700 : 400,
                        }}
                      />
                    </ListItemButton>
                  </StyledListItem>
                );
              })
                : null}
            </List>
          </Box>
        </Box>
      </FullWidthDrawer>
    </>
  );
};

export default ShopNavbar;
